import React from "react";
import { useNavigate } from "react-router-dom";

const CollectionsSection = () => {
  const navigate = useNavigate();

  const collections = [
    {
      title: "Country Collection",
      img: "/assets/Images/1l.webp",
      description: "Experience rustic charm with our exclusive designs that bring warmth and tradition to your home.",
      features: ["Rustic Finish", "Natural Wood", "Vintage Hardware"],
      color: "from-amber-500 to-orange-600",
      category: "country-kitchen"
    },
    {
      title: "Contemporary Collection",
      img: "/assets/Images/2l.webp",
      description: "Explore the perfect blend of tradition and modernity with sleek, timeless designs.",
      features: ["Sleek Lines", "Mixed Materials", "Minimalist Design"],
      color: "from-blue-500 to-cyan-600",
      category: "contemporary-kitchen"
    },
    {
      title: "Modern Collection",
      img: "/assets/Images/3l.webp",
      description: "Innovative designs and smart solutions for contemporary urban living spaces.",
      features: ["Smart Storage", "Premium Finish", "European Style"],
      color: "from-gray-600 to-gray-800",
      category: "modern-kitchen"
    },
    {
      title: "Urban Collection",
      img: "/assets/Images/4l.webp",
      description: "Sophisticated styles inspired by metropolitan life and modern architecture.",
      features: ["Urban Chic", "Compact Design", "Luxury Finish"],
      color: "from-purple-500 to-pink-600",
      category: "urban-kitchen"
    },
  ];

  const handleExploreCollection = (category) => {
    navigate(`/catalog?category=${category}`);
  };

const handleBookConsultation = () => {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: 'smooth' // Optional: adds smooth scrolling
  });
  navigate('/contact');
};

  const handleViewAllCollections = () => {
    navigate('/catalog');
  };

  return (
    <section
      className="py-20 bg-gradient-to-br from-white to-gray-50 relative overflow-hidden"
      aria-labelledby="collections-heading"
      itemScope
      itemType="https://schema.org/ItemList"
    >
      {/* Structured Data for Collections */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "Kitchen Design Collections",
          "description": "Premium kitchen design collections including Country, Contemporary, Modern, and Urban styles",
          "numberOfItems": collections.length,
          "itemListElement": collections.map((collection, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "item": {
              "@type": "ProductCollection",
              "name": collection.title,
              "description": collection.description,
              "keywords": collection.features.join(", "),
              "url": `${window.location.origin}/catalog?category=${collection.category}`
            }
          }))
        })}
      </script>

      {/* Background Decorative Elements */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-orange-100 rounded-full -translate-x-1/2 -translate-y-1/2 opacity-60" aria-hidden="true"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-amber-50 rounded-full translate-x-1/3 translate-y-1/3 opacity-70" aria-hidden="true"></div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <header className="text-center mb-16 px-4">
          <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full mb-4">
            <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" aria-hidden="true"></div>
            <span className="text-sm font-semibold">Premium Collections</span>
          </div>
          
          <h2
            id="collections-heading"
            className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent mb-4"
            itemProp="name"
          >
            Our Exclusive Collections
          </h2>
          
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed" itemProp="description">
            Discover the perfect blend of style, functionality, and craftsmanship in our carefully curated kitchen collections.
          </p>
          
          {/* Decorative Line */}
          <div className="flex justify-center mt-6" aria-hidden="true">
            <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full"></div>
          </div>
        </header>

        {/* Collections Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8 px-4" itemProp="itemListElement">
          {collections.map((collection, index) => (
            <article
              key={index}
              className="group relative overflow-hidden rounded-3xl bg-white shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 focus-within:ring-4 focus-within:ring-orange-500/50"
              aria-labelledby={`collection-title-${index}`}
              itemScope
              itemType="https://schema.org/ProductCollection"
            >
              {/* Image Container */}
              <div className="relative h-80 overflow-hidden">
                <img
                  src={collection.img}
                  alt={`${collection.title} - Premium kitchen design featuring ${collection.features.join(', ')}`}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  itemProp="image"
                  loading="lazy"
                />
                
                {/* Gradient Overlay */}
                <div className={`absolute inset-0 bg-gradient-to-t ${collection.color} opacity-0 group-hover:opacity-90 transition-opacity duration-500`} aria-hidden="true"></div>
                
                {/* Number Badge */}
                <div className="absolute top-4 left-4 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg" aria-hidden="true">
                  <span className="font-bold text-gray-800">{index + 1}</span>
                </div>
                
                {/* Hover Content */}
                <div className="absolute inset-0 flex flex-col justify-end p-6 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <h3
                    id={`collection-title-${index}`}
                    className="text-2xl font-bold text-white mb-3 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500"
                    itemProp="name"
                  >
                    {collection.title}
                  </h3>
                  
                  <p className="text-white/90 text-sm mb-4 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500 delay-100" itemProp="description">
                    {collection.description}
                  </p>
                  
                  {/* Features List */}
                  <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500 delay-200">
                    <div className="flex flex-wrap gap-2 mb-4" itemProp="keywords">
                      {collection.features.map((feature, featureIndex) => (
                        <span
                          key={featureIndex}
                          className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white text-xs font-medium"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                    
                    {/* CTA Button */}
                    <button 
                      onClick={() => handleExploreCollection(collection.category)}
                      className="w-full bg-white text-gray-900 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors duration-300 flex items-center justify-center gap-2 group/btn focus:outline-none focus:ring-4 focus:ring-white/50"
                      aria-label={`Explore ${collection.title} kitchen designs - View in catalog`}
                      itemProp="url"
                    >
                      Explore Collection
                      <svg 
                        className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Default Content (Visible when not hovering) */}
              <div className="p-6 group-hover:opacity-0 transition-opacity duration-300">
                <h3 className="text-xl font-bold text-gray-900 mb-2" itemProp="name">
                  {collection.title}
                </h3>
                <p className="text-gray-600 text-sm line-clamp-2" itemProp="description">
                  {collection.description}
                </p>
                
                {/* View Details Link */}
                <button 
                  onClick={() => handleExploreCollection(collection.category)}
                  className="mt-4 text-orange-600 font-semibold text-sm flex items-center gap-1 group/link hover:text-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 rounded-lg px-2 py-1"
                  aria-label={`View details for ${collection.title}`}
                >
                  View Details
                  <svg 
                    className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </article>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16 px-4">
          <div 
            className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl p-8 text-white max-w-4xl mx-auto shadow-2xl"
            itemScope
            itemType="https://schema.org/Service"
          >
            <meta itemProp="serviceType" content="Kitchen Design Consultation" />
            
            <h3 className="text-2xl md:text-3xl font-bold mb-4" itemProp="name">
              Can't Find What You're Looking For?
            </h3>
            <p className="text-white/90 mb-6 text-lg" itemProp="description">
              Let our design experts create a custom solution tailored to your unique style and space.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={handleBookConsultation}
                className="bg-white text-orange-600 px-8 py-4 rounded-xl font-bold hover:bg-gray-100 transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-white/50"
                aria-label="Book Free Kitchen Design Consultation - Redirects to Contact Page"
                itemProp="potentialAction"
              >
                Book Free Consultation
              </button>
              <button 
                onClick={handleViewAllCollections}
                className="border-2 border-white text-white px-8 py-4 rounded-xl font-bold hover:bg-white hover:text-orange-600 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-white/50"
                aria-label="View All Kitchen Collections - Redirects to Catalog Page"
              >
                View All Collections
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CollectionsSection;